import { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import api from "../lib/api";
import { useNativeLaunch } from "../lib/useNativeLaunch";
import WebPlayer from "../components/WebPlayer";
import { ArrowLeft, MonitorPlay, RotateCw, SkipForward } from "lucide-react";

export default function EpisodePlayer() {
  const { id } = useParams();
  const nav = useNavigate();
  const [ep, setEp] = useState(null);
  const [show, setShow] = useState(null);
  const [nextEp, setNextEp] = useState(null);
  const { status, launch, configReady, lastPlayer } = useNativeLaunch();
  const [native, setNative] = useState(() => localStorage.getItem("kino_player_pref") === "kodi");
  const [reason, setReason] = useState("");
  const playerLabel = lastPlayer === "wmp" ? "Windows Media Player" : "Kodi";
  const otherPlayer = lastPlayer === "wmp" ? "kodi" : "wmp";
  const otherPlayerLabel = otherPlayer === "wmp" ? "Windows Media Player" : "Kodi";
  const useWebPlayer = () => { localStorage.setItem("kino_player_pref", "web"); setReason(""); setNative(false); };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data: e } = await api.get(`/episodes/${id}`);
      if (cancelled) return;
      setEp(e);
      const [{ data: s }, { data: siblings }] = await Promise.all([
        api.get(`/shows/${e.show_id}`),
        api.get(`/shows/${e.show_id}/episodes`),
      ]);
      if (cancelled) return;
      setShow(s);
      const idx = siblings.findIndex((x) => x.id === e.id);
      setNextEp(idx >= 0 && idx < siblings.length - 1 ? siblings[idx + 1] : null);
    })();
    return () => { cancelled = true; };
  }, [id]);

  // Auto-launch once the episode AND the LAN-url config are both ready — launching before
  // configReady would silently fall back to window.location.origin (the public domain, if
  // that's what the browser session is on) instead of the LAN address.
  useEffect(() => {
    if (native && ep && configReady) launch(ep.id, "episode");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ep, configReady, native]);

  if (!ep || !show) return <div className="min-h-screen bg-black flex items-center justify-center text-[#8A8A8A]">Loading…</div>;

  const epLabel = `S${ep.season_number}·E${ep.episode_number} · ${ep.title}`;
  if (!native) {
    return (
      <WebPlayer key={ep.id} kind="episode" id={ep.id} title={show.title} subtitle={epLabel}
        next={nextEp ? { id: nextEp.id, title: `S${nextEp.season_number}·E${nextEp.episode_number} · ${nextEp.title}` } : null}
        onKodi={(why) => { if (!why) localStorage.setItem("kino_player_pref", "kodi"); setReason(why || ""); setNative(true); }} />
    );
  }

  return (
    <div className="fixed inset-0 bg-black z-50 flex flex-col items-center justify-center gap-6 px-6" data-testid="episode-player-page">
      <button onClick={() => nav(-1)} className="absolute top-6 left-6 flex items-center gap-2 text-white/80 hover:text-white bg-black/60 hover:bg-black px-4 py-2 transition-colors" data-testid="ep-player-back">
        <ArrowLeft size={16} strokeWidth={1.5} /> <span className="text-xs uppercase tracking-[0.2em]">Back</span>
      </button>

      <div className="text-center">
        <h2 className="font-display text-lg text-white">{show.title}</h2>
        {reason && <p className="text-[#8A8A8A] text-xs max-w-md mt-2">{reason}</p>}
        <p className="text-[10px] uppercase tracking-[0.3em] text-[#8A8A8A] mt-1">S{ep.season_number}·E{ep.episode_number} · {ep.title}</p>
      </div>

      {status === "launching" && (
        <div className="flex flex-col items-center gap-3" data-testid="ep-player-launching">
          <div className="h-8 w-8 border-2 border-[#333] border-t-[#D9381E] rounded-full animate-spin" />
          <p className="text-[#8A8A8A] text-sm">Opening in {playerLabel}…</p>
        </div>
      )}

      {status === "launched" && (
        <div className="flex flex-col items-center gap-4" data-testid="ep-player-launched">
          <MonitorPlay size={40} strokeWidth={1.5} className="text-[#86efac]" />
          <p className="text-white text-sm">Opened in {playerLabel}. You can close this tab.</p>
        </div>
      )}

      {status === "failed" && (
        <div className="flex flex-col items-center gap-4 max-w-md text-center" data-testid="ep-player-install-prompt">
          <MonitorPlay size={40} strokeWidth={1.5} className="text-[#fca5a5]" />
          <p className="text-white">Couldn't find {playerLabel} or the StreamHoard Player helper on this computer.</p>
          {lastPlayer === "wmp" ? (
            <p className="text-[#8A8A8A] text-sm">
              Windows Media Player isn't installed by default on current Windows 10/11 — add it via
              Settings → Apps → Optional features → Add a feature → "Windows Media Player". Or try
              Kodi instead (full HEVC/DTS/Dolby Vision support).
            </p>
          ) : (
            <p className="text-[#8A8A8A] text-sm">Install Kodi, then try again — or try Windows Media Player instead, already installed on most Windows machines (simpler content only, no HEVC/DTS/Dolby Vision support).</p>
          )}
          {lastPlayer !== "wmp" && (
            <a href="https://kodi.tv/download" target="_blank" rel="noreferrer"
              className="bg-[#D9381E] hover:bg-[#ED4B32] text-white px-6 py-3 text-sm uppercase tracking-[0.2em] text-center"
              data-testid="ep-player-download-kodi">
              Download Kodi
            </a>
          )}
          <p className="text-[#666] text-xs">
            Kodi also needs the StreamHoard Player helper on this Windows PC (it registers the
            streamhoardplay:// link). Install it once by opening{" "}
            <span className="text-[#aaa] select-all">{"\u005c\u005c10.48.200.249\u005cvideos\u005cdownloads\u005cStreamHoard-Player"}</span>{" "}
            on your network and running <b>Install-StreamHoard-Player.cmd</b>, then try again.
          </p>
        </div>
      )}

      {/* Every player-choice / retry action lives together in this one group. */}
      <div className="flex flex-wrap items-center justify-center gap-2" data-testid="ep-player-actions">
        {(status === "launched" || status === "failed") && (
          <button onClick={() => launch(ep.id, "episode", lastPlayer)}
            className="flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] text-[#8A8A8A] hover:text-white border border-[#222] hover:border-white px-4 py-2 transition-colors"
            data-testid={status === "launched" ? "ep-player-relaunch" : "ep-player-retry"}>
            <RotateCw size={13} strokeWidth={1.5} /> {status === "launched" ? "Open again" : "Try again"}
          </button>
        )}
        {(status === "launched" || status === "failed") && (
          <button onClick={() => launch(ep.id, "episode", otherPlayer)}
            className="flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] text-[#8A8A8A] hover:text-white border border-[#222] hover:border-white px-4 py-2 transition-colors"
            data-testid={status === "launched" ? "ep-player-use-wmp" : "ep-player-try-wmp"}>
            {status === "launched" ? "Play" : "Try"} in {otherPlayerLabel} instead
          </button>
        )}
        <button onClick={useWebPlayer}
          className="flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] text-[#8A8A8A] hover:text-white border border-[#222] hover:border-white px-4 py-2 transition-colors"
          data-testid="ep-player-use-web">
          Use web player
        </button>
        {status === "launched" && nextEp && (
          <Link to={`/watch/episode/${nextEp.id}`}
            className="flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] text-[#8A8A8A] hover:text-white border border-[#222] hover:border-white px-4 py-2 transition-colors"
            data-testid="ep-player-next">
            <SkipForward size={13} strokeWidth={1.5} /> Next episode
          </Link>
        )}
      </div>
    </div>
  );
}
